import React, { useState } from "react";
import { FaStar, FaClock, FaUserGraduate, FaPlayCircle } from "react-icons/fa";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { BsPerson } from "react-icons/bs";

const CourseContent = ({ course }) => {
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-md">
      <div className="relative">
        <img
          src={course.banner}
          alt="Course cover"
          className="w-full h-60 sm:h-72 lg:h-80 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent" />
        <div className="absolute left-4 sm:left-6 bottom-4 sm:bottom-6 text-white max-w-2xl">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug">
            {course.title}
          </h1>
          <p className="mt-2 text-xs sm:text-sm text-slate-100/90">
            {course.short}
          </p>
        </div>
      </div>

      <div className="p-5 sm:p-6 lg:p-8 space-y-6">
        {/* Top meta */}
        <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-slate-200/90">
          <div className="flex items-center gap-2">
            <FaStar className="text-yellow-400" />
            <span className="font-medium text-white">
              {course.rating} • {course.students.toLocaleString()} students
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FaClock className="text-[#5989C8]" />
            <span>{course.duration}</span>
          </div>
          <div className="flex items-center gap-2">
            <FaUserGraduate className="text-emerald-400" />
            <span>{course.level}</span>
          </div>
        </div>

        {/* What you'll learn */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-white mb-3">
            What you&apos;ll get
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {course.whatYouGet.map((w, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-2xl border border-white/10 px-3 py-2"
              >
                <span className="mt-1 bg-[#064585]/10 text-[#64a2ff] rounded-full p-2 shadow-lg shadow-[#064585]/30">
                  <FaPlayCircle />
                </span>
                <p className="text-sm sm:text-base font-medium text-slate-100">
                  {w}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Course details / accordion */}
        <section>
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-base sm:text-lg font-semibold text-white">
              Course Details
            </h2>
            <p className="text-xs sm:text-sm text-slate-300">
              {course.courseContent.length} sections • {course.duration}
            </p>
          </div>

          <div className="mt-4 space-y-3">
            {course.courseContent.map((c) => (
              <div
                key={c.id}
                className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => toggle(c.id)}
                  className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 text-left"
                >
                  <p className="font-medium text-sm sm:text-base text-slate-100">
                    {c.title}
                  </p>
                  <div className="text-slate-300">
                    {expanded === c.id ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                </button>

                {expanded === c.id && (
                  <ul className="divide-y divide-white/5 px-4 sm:px-6 pb-2 sm:pb-3">
                    {c.lessons.map((l, idx) => (
                      <li key={idx} className="py-3 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#064585]/15 flex items-center justify-center text-xs font-semibold text-[#64a2ff]">
                          {idx + 1}
                        </div>
                        <p className="text-sm sm:text-base text-slate-100">
                          {l}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-white">
            Student reviews
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {course.reviews.map((r) => (
              <div
                key={r.id}
                className="bg-white/5 border border-white/10 p-4 rounded-2xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#064585]/20 flex items-center justify-center text-[#64a2ff]">
                    <BsPerson />
                  </div>
                  <div>
                    <p className="font-medium text-slate-100">{r.name}</p>
                    <div className="flex items-center text-yellow-400 text-xs">
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <FaStar key={i} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-200/90">{r.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CourseContent;
